package org.gymly.util

import kotlin.time.Clock
import kotlin.time.Instant

/** Semana ISO (lun–dom) a partir de un offset respecto a la semana actual (0 = esta). */
data class WeekRange(
    val mondayIso: String,
    val sundayIso: String,
    val weekOffset: Int,
) {
    fun labelEs(): String {
        val a = formatDayMonth(mondayIso)
        val b = formatDayMonth(sundayIso)
        return when (weekOffset) {
            0 -> "Esta semana · $a – $b"
            -1 -> "Semana pasada · $a – $b"
            1 -> "Próxima semana · $a – $b"
            else -> "Semana · $a – $b"
        }
    }

    /** Fecha ISO del día (1=lun … 7=dom) dentro de esta semana. */
    fun dateForWeekday(weekdayIso: Int): String {
        val day = weekdayIso.coerceIn(1, 7)
        return isoFromEpochDay(daysSinceEpoch(mondayIso) + (day - 1))
    }

    fun containsIsoDate(isoDate: String?): Boolean {
        val d = isoDate?.take(10) ?: return false
        return d >= mondayIso && d <= sundayIso
    }

    companion object {
        fun ofOffset(weekOffset: Int, todayIso: String = todayIsoDate()): WeekRange {
            val monday = mondayOfWeek(todayIso)
            val mondayDay = daysSinceEpoch(monday) + weekOffset * 7L
            val sunDay = mondayDay + 6
            return WeekRange(
                mondayIso = isoFromEpochDay(mondayDay),
                sundayIso = isoFromEpochDay(sunDay),
                weekOffset = weekOffset,
            )
        }

        /** Offset de semanas respecto a la semana actual para un lunes ISO dado. */
        fun offsetForMonday(mondayIso: String): Int {
            val target = mondayOfWeek(mondayIso.take(10))
            val current = mondayOfWeek(todayIsoDate())
            return ((daysSinceEpoch(target) - daysSinceEpoch(current)) / 7).toInt()
        }

        fun displayRangeEs(mondayIso: String): String {
            val monday = mondayOfWeek(mondayIso.take(10))
            val sunday = isoFromEpochDay(daysSinceEpoch(monday) + 6)
            return "${formatDayMonth(monday)} – ${formatDayMonth(sunday)}"
        }
    }
}

/** Zona operativa fija de Gymly (Venezuela, UTC−04:00, sin DST). */
const val GYMLY_TIMEZONE_ID = "America/Caracas"
private const val CARACAS_OFFSET_SECONDS = -4L * 3600L

/** Fecha de hoy en zona Venezuela (`yyyy-MM-dd`), no UTC del Instant. */
fun todayIsoDate(): String {
    val caracasEpoch = Clock.System.now().epochSeconds + CARACAS_OFFSET_SECONDS
    return Instant.fromEpochSeconds(caracasEpoch).toString().take(10)
}

/**
 * Día operativo Gymly (`yyyy-MM-dd` en America/Caracas) a partir de un Instant ISO
 * o de una fecha `yyyy-MM-dd`. Evita que `paidAt.take(10)` use el día UTC.
 */
fun gymlyDateIsoFromInstant(raw: String?): String? {
    val trimmed = raw?.trim().orEmpty()
    if (trimmed.isEmpty()) return null
    if (trimmed.length == 10 && trimmed[4] == '-' && trimmed[7] == '-') {
        return trimmed
    }
    val normalized = trimmed.replace(' ', 'T').let { t ->
        when {
            t.endsWith("Z", ignoreCase = true) -> t
            Regex("[+-]\\d{2}:\\d{2}$").containsMatchIn(t) -> t
            t.contains('T') -> "${t}Z"
            else -> t
        }
    }
    return runCatching {
        val instant = Instant.parse(normalized)
        Instant.fromEpochSeconds(instant.epochSeconds + CARACAS_OFFSET_SECONDS).toString().take(10)
    }.getOrElse {
        trimmed.take(10).takeIf { it.length >= 10 && it[4] == '-' }
    }
}

/**
 * «Desde» por defecto de un listado: hoy menos 6 días, así el rango «Desde–Hasta» cubre 7 días con hoy incluido
 * (el mismo criterio que los reportes).
 */
fun desdeSieteDiasIso(todayIso: String = todayIsoDate()): String =
    isoFromEpochDay(daysSinceEpoch(todayIso) - 6)

/** Primer día del mes actual en ISO `yyyy-MM-dd` (p. ej. filtros «Desde»). */
fun firstDayOfCurrentMonthIso(todayIso: String = todayIsoDate()): String {
    val parts = todayIso.take(10).split("-")
    if (parts.size != 3) return todayIso.take(10)
    val y = parts[0]
    val m = parts[1]
    return "$y-$m-01"
}

private fun daysSinceEpoch(isoDate: String): Long {
    val instant = Instant.parse("${isoDate.take(10)}T00:00:00Z")
    return instant.epochSeconds / 86_400L
}

private fun isoFromEpochDay(epochDay: Long): String {
    val instant = Instant.fromEpochSeconds(epochDay * 86_400L)
    return instant.toString().take(10)
}

/** 1 = lunes … 7 = domingo (ISO). 1970-01-01 = jueves. */
private fun isoWeekday(epochDay: Long): Int {
    val zeroBasedMon = ((epochDay + 3) % 7).toInt()
    return zeroBasedMon + 1
}

fun mondayOfWeek(isoDate: String): String {
    val ed = daysSinceEpoch(isoDate)
    val dow = isoWeekday(ed)
    return isoFromEpochDay(ed - (dow - 1))
}

/** Día ISO (1 = lunes … 7 = domingo) de una fecha yyyy-MM-dd. */
fun isoWeekdayOf(isoDate: String): Int = isoWeekday(daysSinceEpoch(isoDate))

private fun formatDayMonth(iso: String): String {
    val parts = iso.take(10).split("-")
    val day = parts.getOrNull(2)?.toIntOrNull()?.toString() ?: iso
    val month = parts.getOrNull(1)?.toIntOrNull()?.let { monthShortEs[it] } ?: ""
    return "$day $month"
}

private val monthShortEs: Map<Int, String> = mapOf(
    1 to "ene", 2 to "feb", 3 to "mar", 4 to "abr", 5 to "may", 6 to "jun",
    7 to "jul", 8 to "ago", 9 to "sep", 10 to "oct", 11 to "nov", 12 to "dic",
)
